import React from 'react';
import { TemplateType } from '../TemplateType';
import usePdfContent, { PdfContentParams } from '../usePdfContent';

import Grid from '@material-ui/core/Grid';

import PdfContent from 'apps/Pdf/components/common/PdfContent';
import Title from 'apps/Pdf/components/common/Title';
import H1 from 'apps/Pdf/components/common/H1';
import H2 from 'apps/Pdf/components/common/H2';
import H3 from 'apps/Pdf/components/common/H3';
import Loading from 'apps/Pdf/components/common/Loading';
import P from 'apps/Pdf/components/common/P';
import Spacer from 'apps/Pdf/components/common/Spacer';
import Article from 'apps/Pdf/components/common/Article';

import PersonalData from 'apps/Pdf/templates/MainContract/components/PersonalData';
import CompanyData from 'apps/Pdf/templates/MainContract/components/CompanyData';
import useGeneralStyles from 'apps/Pdf/styles/general';
import useColorStyles from 'apps/Pdf/styles/colors';
import Signature from 'apps/Pdf/components/Signature/Signature';
import { LTIPContent } from './LTIPInterface';
import { ContractPhysicalData, ContractLegalData } from 'stores/workflowStores/workflowPersonalDataStore/model';
import { PersonType } from 'enums';
import { PdfCategory } from '../Interfaces';

const LTIP = (params: PdfContentParams) => {
  const styles = useGeneralStyles();

  const pdfContent = usePdfContent<LTIPContent>({ templateType: TPL_LTIP, category: PdfCategory.SIGNATURE, ...params });
  const data = pdfContent?.data ? pdfContent.data : ({} as LTIPContent);
  const legal = pdfContent?.data && data.clientData.personType === PersonType.LEGAL;
  const colors = useColorStyles({ code: pdfContent?.data?.header.signedVia || '' });

  return pdfContent ? (
    <PdfContent errors={pdfContent?.errors} className={colors.root} onload afterLoad={params.afterLoad}>
      <Title>Smlouva o dlouhodobém investičním produktu</Title>
      <P className="tighter">
        Tato Smlouva o dlouhodobém investičním produktu (dále jen „Smlouva“) byla uzavřena mezi:
      </P>
      <H2>Zákazník:</H2>
      {legal ? (
        <CompanyData data={data.clientData as ContractLegalData} />
      ) : (
        <PersonalData data={data.clientData as ContractPhysicalData} />
      )}

      {data.clientData.employerData?.employers.length && (
        <>
          <H3 className="inLines">Zaměstnavatelé</H3>
          <P>
            {data.clientData.employerData.employers.map((e) => (
              <>
                {e.companyName}, IČO: {e.companyNumber}
                <br />
              </>
            ))}
          </P>
        </>
      )}

      {legal && (
        <>
          <Spacer variant="half" />
          <H2>Zástupce zákazníka:</H2>
          {'representative' in data.clientData && <PersonalData data={data.clientData.representative} />}
        </>
      )}
      <Spacer variant="half" />
      <H2>WOOD RI:</H2>
      <P>
        <strong>WOOD Retail Investments a.s.</strong>, Jihlavská 1558/21, Michle, 140 00 Praha 4, IČO 17818834, zapsaná
        v obchodním rejstříku vedeném Městským soudem v Praze, oddíl B, vložka 27805, zastoupená{' '}
        <strong>Radimem Krejčím, předsedou představenstva</strong>.
      </P>
      <P>(dále jen „WOOD RI“)</P>
      <P>(WOOD RI a Zákazník společně dále též „Smluvní strany“ a jednotlivě jako „Smluvní strana“)</P>

      <div className={`${styles.twoColumns} ${styles.lines}`}>
        <Grid container>
          <Grid item xs={4}>
            Investiční zprostředkovatel:
          </Grid>
          <Grid item xs={8} className={styles.value}>
            {data.header.intermediaryCompany.companyName}
          </Grid>

          {data.header.advisor.role === 'DEPUTY' && (
            <>
              <Grid item xs={4}>
                Vázaný zástupce:
              </Grid>
              <Grid item xs={8} className={styles.value}>
                {data.header.partnerCompany.companyName}
              </Grid>
            </>
          )}
          <Grid item xs={4}>
            {data.header.advisor.role === 'DEPUTY' || data.header.advisor.role === 'DIRECT' ? 'Poradce' : 'Vázaný zástupce'}:
          </Grid>
          <Grid item xs={8} className={`${styles.bold} ${styles.value}`}>
            {data.header.advisor.firstname} {data.header.advisor.lastname}
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={4}>
            Osobní kód:
          </Grid>
          <Grid item xs={8} className={styles.value}>
            {data.header.advisor.code}
          </Grid>
          <Grid item xs={4}>
            Telefon:
          </Grid>
          <Grid item xs={8} className={styles.value}>
            {data.header.advisor.contacts.phone}
          </Grid>
          <Grid item xs={4}>
            E-mail:
          </Grid>
          <Grid item xs={8} className={styles.value}>
            {data.header.advisor.contacts.email}
          </Grid>
        </Grid>
      </div>

      <P>
        Následující smluvní ujednání navazují na Rámcovou smlouvu o poskytování investičních služeb, na základě které
        WOOD RI Zákazníkovi poskytuje specifikované investiční služby („Rámcová smlouva“), a na všechny její součásti a
        tvoří spolu s Produktovými podmínkami pro Důchodový účet WOOD („Produktové podmínky“) tuto Smlouvu, na základě
        které se WOOD RI pro Zákazníka zavazuje vést majetkový účet v režimu dlouhodobého investičního produktu.
      </P>

      <P>
        Pojmy a slovní spojení používané v této Smlouvě, které nejsou touto Smlouvou definovány, mají význam definovaný
        v Rámcové smlouvě, Produktových podmínkách či Obchodních podmínkách („Podmínky“).
      </P>

      <H1 numbered>Uzavření smlouvy</H1>
      <Article numbered levelUp>
        Tato Smlouva byla vygenerována automaticky na základě údajů Zákazníka, které poskytl prostřednictvím online
        komunikace. Smlouva nabývá platnosti a účinnosti po jejím podpisu oběma Smluvními stranami, přičemž k podpisu
        může dojít i elektronicky (online); nikoli však dříve, než nastane platnost a účinnost Rámcové smlouvy.
      </Article>

      <H1 numbered>Předmět smlouvy</H1>
      <Article numbered levelUp>
        WOOD RI zřizuje na základě této Smlouvy pro Zákazníky Účet WOOD DIP, který je veden odděleně od jiných Peněžních
        účtů a Účtů cenných papírů Zákazníka, nezávisle na nich a podléhá zvláštním podmínkám. Veškeré prostředky vedené
        na Účtu WOOD DIP (ať už peněžní prostředky, či Investiční nástroje) podléhají režimu WOOD DIP, který se řídí
        touto Smlouvou a Produktovými podmínkami. Neurčí-li tato Smlouva nebo Produktové podmínky jinak, podpůrně se
        použijí pro Účet WOOD DIP ustanovení Podmínek.
      </Article>
      <Article numbered levelUp>
        Účet WOOD DIP je poskytován jako dlouhodobý investiční produkt ve smyslu § 134g s násl. Zákona o podnikání na
        kapitálovém trhu. Jako takový spadá do kategorie produktů spoření na stáří ve smyslu Zákona o daních z příjmů. V
        důsledku toho je za splnění zákonných podmínek WOOD DIP daňově podporovaný. Konkrétní podoba Daňové podpory
        vyplývá ze Zákona o daních z příjmů a je popsána v Produktových podmínkách.
      </Article>
      <Article numbered levelUp>
        V rámci WOOD DIP mohou být poskytovány hlavní investiční služby (a) Obhospodařování Portfolia dle
        Obhospodařovatelské smlouvy a/nebo (b) přijímání a předávání pokynů týkajících se Investičních nástrojů v podobě
        závazku WOOD RI obstarat pro Zákazníka koupi nebo prodej Investičních nástrojů, nebo vyvíjet činnost směřující
        ke koupi nebo prodeji Investičních nástrojů podle závazných Pokynů Zákazníka podle Komisionářské smlouvy, a to v
        závislosti na tom, jaké investiční služby jsou Zákazníkovi poskytovány na základě Rámcové smlouvy.
      </Article>

      <H1 numbered>Prohlášení zákazníka</H1>
      <Article numbered levelUp>
        Zákazník výslovně prohlašuje, že byl seznámen s Produktovými podmínkami a jejich významem v dostatečném
        předstihu před uzavřením Smlouvy. Zákazník rovněž prohlašuje, že s WOOD RI uzavírá či uzavřel Rámcovou smlouvu a
        s jejím obsahem a všemi dokumenty, na které Rámcová smlouva odkazuje, souhlasí.
      </Article>
      <Article numbered levelUp>
        Zákazník výslovně prohlašuje, že si je vědom skutečnosti, že za poskytnuté služby obdrží WOOD RI odměnu podle
        Produktových podmínek platných po dobu trvání Smlouvy v rozhodné době.
      </Article>
      <Article numbered levelUp>
        Zákazník prohlašuje, že si je vědom, že obdržení Daňové podpory je podmíněno naplněním podmínek Zákona o daních
        z příjmů. Zákazník zejména pro zachování Daňové podpory nesmí vybrat prostředky z WOOD DIP (a to ani částečně)
        dříve než za splnění následujících podmínek:
        <ul>
          <li>nastane kalendářní rok, ve kterém Zákazník dosáhne 60 let věku; a zároveň</li>
          <li>uplyne 120 měsíců od vzniku jeho WOOD DIP.</li>
        </ul>
        Bližší popis podmínek obdržení Daňové podpory, včetně popisu způsobu počítání lhůty od vzniku WOOD DIP, je
        uveden v Produktových podmínkách.
      </Article>
      <Article numbered levelUp>
        Zákazník uděluje svůj výslovný souhlas, aby mu byly informace poskytovány na jiném trvalém nosiči dat než na
        papíru, pokud to platné právní předpisy připouštějí. Zákazník se v tomto rozsahu výslovně vzdává práva na
        zasílání poskytovaných dokumentů poštou. Informační povinnosti společnosti WOOD RI vůči Zákazníkovi mohou být
        plněny i v jiné než v listinné podobě, tj. elektronicky (např. e-mailem), přičemž Zákazník souhlasí s tím, že
        elektronicky (např. e-mailem), telefonicky nebo jinými prostředky komunikace na dálku (např. prostřednictvím
        internetu) může mezi ním a společností WOOD RI probíhat i jejich vzájemná komunikace při poskytování služeb
        podle této Smlouvy.
      </Article>
      <Article numbered levelUp>
        Zákazník tímto výslovně přijímá ustanovení Produktových podmínek označené jako „Podmínky daňové podpory“,
        „Předčasný výběr“, „Případy Předčasného výběru“, „Následky Předčasného výběru a odpovědnost WOOD RI“, „Započtení“ a
        „Odepsání prostředků z Účtu WOOD DIP“ nebo obdobným způsobem.
      </Article>

      <H1 numbered>Zánik smlouvy</H1>
      <Article numbered levelUp>
        Smlouva může být ukončena:
        <Article numbered>písemnou dohodou Smluvních stran ke dni stanovenému v takové dohodě;</Article>
        <Article numbered>
          výpovědí kteroukoli Smluvní stranou bez udání důvodu s jednoměsíční výpovědní dobou, která začíná běžet prvním
          dnem měsíce následujícího po měsíci, v němž byla výpověď doručena druhé Smluvní straně; a
        </Article>
        <Article numbered>
          odstoupením od Smlouvy v případech výslovně upravených v Rámcové smlouvě nebo Podmínkách a dále v případech
          stanovených právními předpisy v té míře, v jaké toto nelze vyloučit opačným ujednáním mezi Smluvními stranami,
          které Smluvní strany tímto činí.
        </Article>
      </Article>
      <Article numbered levelUp>
        K zániku WOOD DIP, a tedy i této Smlouvy, dochází rovněž realizací Předčasného výběru nebo Externího převodu ve
        smyslu Produktových podmínek
      </Article>
      <Article numbered levelUp>
        Zákazník nesmí Smlouvu vypovědět nebo jinak ukončit, dokud Účet WOOD DIP slouží k platbě nebo jako zajištění
        pohledávek WOOD RI vyplývajících ze vztahů mezi WOOD RI a Zákazníkem a dokud nebudou splněny veškeré příslušné
        závazky Zákazníka
      </Article>
      <Article numbered levelUp>
        Zánikem této Smlouvy zaniká produkt WOOD DIP. Zánik WOOD DIP může mít ve smyslu Zákona o daních z příjmů za
        následek povinnost Zákazníka navrátit Daňovou podporu. Zánik WOOD DIP před splněním Podmínek daňové podpory se
        považuje za Předčasný výběr, nejedná-li se o Povolený předčasný výběr. V případě zániku produktu WOOD DIP, aniž
        by došlo k zániku Rámcové smlouvy, jsou všechny zbývající prostředky evidované na Účtu WOOD DIP převedeny na
        prospěch Účtu Zákazníka, který nepodléhá režimu WOOD DIP. S takovými prostředky může Zákazník nakládat v režimu
        příslušných ustanovení Rámcové smlouvy a Podmínek. Omezení stanovená touto Smlouvou a Produktovými podmínky se
        po zániku WOOD DIP již neuplatní. Ve zbytku se pro zánik WOOD DIP použijí přiměřeně ustanovení Podmínek označené
        jako „Zánik právních vztahů“ nebo obdobným způsobem. WOOD RI je oprávněna po Zákazníkovi v souvislosti se
        zánikem produktu WOOD DIP požadovat Oznámení o naložení s účtem ve smyslu Podmínek
      </Article>
      <Article numbered levelUp>
        Úmrtím Zákazníka se Účet WOOD DIP automaticky přemění na Účet, který není vázán režimem WOOD DIP. V první
        Pracovní den následující po dni, kdy se WOOD RI z důvěryhodného zdroje dozví o úmrtí Zákazníka (doručení kopie
        úmrtního listu, rozhodnutí soudu) přestane provádět Pokyny a poskytovat Investiční služby, a to až do
        pravomocného skončení dědického řízení, nebude-li platnými právními předpisy stanoveno jinak. Za účelem
        zachování hodnoty pozůstalosti Zákazníka Zákazník výslovně zmocňuje WOOD RI, aby dle vlastní úvahy vyprodalo
        Investiční nástroje na Účtu WOOD DIP (resp. na Účtu, na který se WOOD DIP automaticky přemění) Zákazníka bez
        zbytečného odkladu poté, co se dozví o jeho úmrtí, pokud to uzná za vhodné zejména s ohledem na situaci na
        kapitálových trzích či povahu držených Investičních nástrojů
      </Article>
      <Article numbered levelUp>
        Trvání této Smlouvy je závislé na trvání Rámcové smlouvy. Dojde-li z jakéhokoli důvodu k zániku závazků z
        Rámcové smlouvy, automaticky okamžikem zániku Rámcové smlouvy dochází k zániku této Smlouvy. Zánikem WOOD DIP,
        resp. této Smlouvy, ovšem není dotčeno trvání Rámcové smlouvy, na základě které jsou Zákazníkovi poskytovány
        jiné služby a produkty než WOOD DIP. Rámcová smlouva není v tomto smyslu závislá na této Smlouvě
      </Article>

      <H1 numbered>Další ujednání</H1>
      <Article numbered levelUp>
        WOOD RI je oprávněna využívat při plnění této Smlouvy služeb třetí osoby.
      </Article>
      <Article numbered levelUp>
        Tato Smlouva navazuje na uzavřenou Rámcovou smlouvu a doplňuje ji. Pokud tato Smlouva obsahuje odlišnou úpravu
        od Rámcové smlouvy, mají přednost ustanovení této Smlouvy. Ustanovení této Smlouvy mají rovněž přednost před
        zněním Produktových podmínek.
      </Article>
      <Article numbered levelUp>
        Pokud Zákazník odpoví na tuto Smlouvu s dodatkem či odchylkou, která podstatně nemění podmínky nabídky, nejedná
        se o přijetí nabídky. Odpověď s dodatkem či odchylkou se považuje za novou nabídku, která podléhá schválení ze
        strany WOOD RI.
      </Article>
      <Article numbered levelUp>
        WOOD RI si vyhrazuje právo měnit Produktové podmínky. Způsob oznámení změn v Produktových podmínkách a možnost
        Smlouvu z důvodu takové změny vypovědět jsou specifikovány v Produktových podmínkách.
      </Article>

      <H1>Podpis klienta</H1>
      <P>
        Na důkaz souhlasu s obsahem této Smlouvy připojují Smluvní strany své podpisy a zároveň prohlašují, že tato
        Smlouva byla uzavřena z jejich svobodné a vážné vůle
      </P>

      <P>
        Za <strong>WOOD Retail Investments a.s. Radim Krejčí</strong>
      </P>

      <Signature protocol={data.signedInfo} />
    </PdfContent>
  ) : params.preview ? (
    <Loading />
  ) : null;
};

const TPL_LTIP: TemplateType = 'ltipAgreement';

export { TPL_LTIP, LTIP };
